import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { OrbiHistoryConversation } from '@patient-ui/shared/models';
import {
  formatOrbiConversationDate,
  orbiHistoryDisplayTitle,
  orbiHistoryIsReadOnly,
  orbiHistoryMessageCount,
} from '@patient-ui/shared/utils';

import { OrbiAnnouncerService } from '../../services/orbi-announcer.service';

/** Max title length accepted by the backend rename endpoint. */
export const ORBI_MAX_TITLE_LENGTH = 40;

/** Remaining-count threshold at/below which the counter turns to a warning. */
const ORBI_TITLE_WARN_REMAINING = 5;

const ORBI_TITLE_COUNTDOWN_CHECKPOINTS = [ORBI_TITLE_WARN_REMAINING, 0];

type OrbiHistoryItemFocusTarget =
  | 'menu'
  | 'trigger'
  | 'rename'
  | 'confirm'
  | 'row';

/**
 * One conversation-history row (PCSA-13169). Presentational: shows the derived
 * title, date, message count, and Active / Read-Only pills (all client-side
 * inferences mirroring mobile — the backend exposes none of these as fields).
 *
 * The row doubles as its own inline RENAME editor (per design): the 3-dot menu
 * offers Rename / Delete; choosing Rename morphs the row into an edit card
 * (label + input + live "N characters remaining" counter that warns near the
 * 40-char limit + Cancel/Save). Emits `resumeConversation`,
 * `renameConversation` (with the new title), and `removeConversation`; the
 * container owns the store dispatch.
 */
@Component({
  selector: 'patient-ui-orbi-history-item',
  templateUrl: './orbi-history-item.component.html',
  styleUrls: ['./orbi-history-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiHistoryItemComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  @Input() conversation!: OrbiHistoryConversation;
  /** Session id of the conversation currently open in the thread. */
  @Input() activeSessionId: string | null = null;
  /**
   * Whether the AI Agent is locked for the session (PCSA-13170, mobile parity).
   * While locked, every NON-active row is grayed out + non-tappable and hides
   * its options menu, so history cannot be used to resume another chat and
   * bypass the lock. Mirrors mobile `orbi_history_drawer.dart` (`isSessionLocked`
   * → `AbsorbPointer` + 40% opacity on non-active items).
   */
  @Input() sessionLocked = false;

  // Output names avoid standard DOM event names (resume/rename/delete are
  // rejected by @angular-eslint/no-output-native), hence the *Conversation
  // suffixes. Rename carries the new title.
  @Output() resumeConversation = new EventEmitter<OrbiHistoryConversation>();
  @Output() renameConversation = new EventEmitter<{
    conversation: OrbiHistoryConversation;
    title: string;
  }>();
  @Output() removeConversation = new EventEmitter<OrbiHistoryConversation>();

  @ViewChild('row') private row?: ElementRef<HTMLButtonElement>;
  @ViewChild('menuBtn') private menuBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('menuWrap') private menuWrap?: ElementRef<HTMLElement>;
  @ViewChildren('menuItem') private menuItems?: QueryList<
    ElementRef<HTMLButtonElement>
  >;
  @ViewChild('renameField') private renameField?: ElementRef<HTMLInputElement>;
  @ViewChild('confirmPanel') private confirmPanel?: ElementRef<HTMLElement>;

  /** Whether the row's 3-dot menu is expanded. */
  menuOpen = false;

  /** Whether the row is in inline-rename edit mode. */
  editing = false;

  /** Whether the row is showing the delete-confirmation step (AC 9a). */
  confirmingDelete = false;

  readonly maxTitleLength = ORBI_MAX_TITLE_LENGTH;
  readonly renameControl = new FormControl<string>('', { nonNullable: true });

  private lastRemaining = ORBI_MAX_TITLE_LENGTH;

  private pendingFocus: OrbiHistoryItemFocusTarget | null = null;

  private readonly destroyed = new Subject<void>();

  constructor(private readonly announcer: OrbiAnnouncerService) {}

  ngOnInit(): void {
    this.renameControl.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((value) => this.onRenameValueChange(value));
  }

  ngAfterViewChecked(): void {
    const target = this.pendingFocus;
    if (!target) {
      return;
    }
    this.pendingFocus = null;
    this.applyFocus(target);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get title(): string {
    return orbiHistoryDisplayTitle(this.conversation);
  }

  get messageCount(): number {
    return orbiHistoryMessageCount(this.conversation);
  }

  get isReadOnly(): boolean {
    return orbiHistoryIsReadOnly(this.conversation);
  }

  get isActive(): boolean {
    return (
      !!this.activeSessionId &&
      this.conversation.metadata.sessionId === this.activeSessionId
    );
  }

  /**
   * Grayed out + non-tappable because the session is locked and this is NOT the
   * active conversation (PCSA-13170, mobile parity). The active (read-only) chat
   * stays reopenable so the user can keep reading it.
   */
  get isDisabledByLock(): boolean {
    return this.sessionLocked && !this.isActive;
  }

  /**
   * Whether the options (3-dot) menu is available. Hidden when the row is grayed
   * out by the session lock (a non-active locked row is inert). A read-only
   * conversation keeps the menu but hides "Rename" (see `showRename`) — mobile
   * gates Rename with `if (!isLocked)` while Delete stays available.
   */
  get showMenu(): boolean {
    return !this.isDisabledByLock;
  }

  /**
   * Whether the "Rename" option is offered. Hidden on a read-only (guardrailed)
   * conversation — mobile gates Rename with `if (!isLocked)`. Delete is still
   * available.
   */
  get showRename(): boolean {
    return !this.isReadOnly;
  }

  get menuLabel(): string {
    return `Options for ${this.title}`;
  }

  get renameFieldId(): string {
    return `orbi-rename-${this.conversation.metadata.sessionId}`;
  }

  get renameCounterId(): string {
    return `${this.renameFieldId}-counter`;
  }

  /** Characters left before the max title length. */
  get charactersRemaining(): number {
    return this.maxTitleLength - this.renameControl.value.length;
  }

  /** True when the remaining count is low enough to warn (turns red). */
  get charactersWarning(): boolean {
    return this.charactersRemaining <= ORBI_TITLE_WARN_REMAINING;
  }

  get counterText(): string {
    return `${this.charactersRemaining} characters remaining`;
  }

  /** Save is enabled only for a non-empty title within the limit. */
  get canSaveRename(): boolean {
    return this.renameControl.value.trim().length > 0;
  }

  /**
   * Created date as MM/DD/YYYY (parity with mobile's DateFormat), and `''` for a
   * conversation that arrives without one.
   *
   * Delegated to `formatOrbiConversationDate`, which keeps the UTC formatting this
   * row has always used, and why. The THREAD dates itself too (design review
   * 08/20) but states it differently — "Today, Aug 21, 2026" per design review
   * 08/21, via `formatOrbiChatDate`. The two formats are separate on purpose; the
   * DAY under them is resolved once in the util, so one conversation cannot show
   * one date in this list and a different one at the top of the chat it opens.
   *
   * No `fallback` is passed, so a dateless row still renders nothing rather than
   * today.
   */
  get dateLabel(): string {
    return formatOrbiConversationDate(this.conversation.metadata?.createdAt);
  }

  onResume(): void {
    // No-op while editing, or when the row is grayed out by the session lock
    // (PCSA-13170, mobile parity — a locked non-active row is non-tappable).
    if (this.editing || this.isDisabledByLock) {
      return;
    }
    this.resumeConversation.emit(this.conversation);
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
    this.pendingFocus = this.menuOpen ? 'menu' : null;
  }

  onMenuKeydown(event: KeyboardEvent): void {
    const items = (this.menuItems ?? []).map((item) => item.nativeElement);

    switch (event.key) {
      case 'ArrowDown':
        this.moveMenuFocus(items, 1);
        break;
      case 'ArrowUp':
        this.moveMenuFocus(items, -1);
        break;
      case 'Home':
        items[0]?.focus();
        break;
      case 'End':
        items[items.length - 1]?.focus();
        break;
      case 'Escape':
        this.closeMenu(true);
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  onMenuFocusOut(event: FocusEvent): void {
    if (!this.menuOpen) {
      return;
    }
    const next = event.relatedTarget as Node | null;
    if (next && this.menuWrap?.nativeElement.contains(next)) {
      return;
    }
    this.menuOpen = false;
  }

  // --- Inline rename --------------------------------------------------------
  startRename(event: Event): void {
    event.stopPropagation();
    this.menuOpen = false;
    this.renameControl.setValue(this.title.slice(0, this.maxTitleLength), {
      emitEvent: false,
    });
    this.lastRemaining = this.charactersRemaining;
    this.editing = true;
    this.pendingFocus = 'rename';
  }

  cancelRename(event: Event): void {
    event.stopPropagation();
    this.editing = false;
    this.pendingFocus = 'trigger';
  }

  saveRename(event: Event): void {
    event.stopPropagation();
    if (!this.canSaveRename) {
      return;
    }

    this.editing = false;
    this.pendingFocus = 'trigger';
    this.renameConversation.emit({
      conversation: this.conversation,
      title: this.renameControl.value.trim(),
    });
  }

  private onRenameValueChange(value: string): void {
    const remaining = this.maxTitleLength - value.length;
    const previous = this.lastRemaining;
    this.lastRemaining = remaining;
    if (remaining === previous) {
      return;
    }

    if (ORBI_TITLE_COUNTDOWN_CHECKPOINTS.includes(remaining)) {
      this.announcer.announce(`${remaining} characters remaining`);
    }
  }

  // --- Delete (with confirmation, AC 9a/b) ----------------------------------
  /** Menu "Delete" opens the confirmation step — it does NOT delete yet. */
  onDelete(event: Event): void {
    event.stopPropagation();
    this.menuOpen = false;
    this.confirmingDelete = true;
    this.pendingFocus = 'confirm';
  }

  /** Cancel the confirmation: the conversation stays (AC 9a/b). */
  cancelDelete(event: Event): void {
    event.stopPropagation();
    this.confirmingDelete = false;
    this.pendingFocus = 'trigger';
  }

  /** Confirm: now emit the delete for the container to dispatch (AC 9a). */
  confirmDelete(event: Event): void {
    event.stopPropagation();
    this.confirmingDelete = false;
    this.pendingFocus = 'row';
    this.removeConversation.emit(this.conversation);
  }

  focusRow(): boolean {
    const row = this.row?.nativeElement;
    if (!row || row.disabled) {
      return false;
    }
    row.focus();
    return true;
  }

  private applyFocus(target: OrbiHistoryItemFocusTarget): void {
    switch (target) {
      case 'menu':
        this.menuItems?.first?.nativeElement.focus();
        break;
      case 'trigger':
        this.menuBtn?.nativeElement.focus();
        break;
      case 'rename':
        this.renameField?.nativeElement.focus();
        break;
      case 'confirm':
        this.confirmPanel?.nativeElement.focus();
        break;
      case 'row':
        this.focusRow();
        break;
    }
  }

  private moveMenuFocus(items: HTMLElement[], step: 1 | -1): void {
    if (!items.length) {
      return;
    }
    const active = items[0].ownerDocument.activeElement;
    const current = items.findIndex((item) => item === active);
    if (current === -1) {
      items[step === 1 ? 0 : items.length - 1].focus();
      return;
    }
    items[(current + step + items.length) % items.length].focus();
  }

  private closeMenu(returnFocus: boolean): void {
    this.menuOpen = false;
    if (returnFocus) {
      this.menuBtn?.nativeElement.focus();
    }
  }
}
